import type { ComponentType, SVGProps } from 'react';

import type { WorkflowTemplate } from '@/services/data/CampaignService';

type ArtworkProps = SVGProps<SVGSVGElement>;
type Artwork = ComponentType<ArtworkProps>;

const WRAPPER = {
  viewBox: '0 0 80 56',
  fill: 'none' as const,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  xmlns: 'http://www.w3.org/2000/svg',
};
const INK = 'stroke-foreground';
const ACCENT = 'fill-[hsl(var(--illustration-accent))] stroke-none';

function ArtworkShell({
  accent,
  primary,
  secondary,
  detail,
  ...props
}: ArtworkProps & { accent: string; primary: string; secondary: string; detail?: string }) {
  return (
    <svg {...WRAPPER} {...props}>
      <path d={accent} className={ACCENT} />
      <path d={primary} className={INK} strokeWidth="2" />
      <path d={secondary} className={INK} strokeWidth="1.5" />
      {detail && <path d={detail} className={INK} strokeWidth="1" />}
    </svg>
  );
}

export function ArticlePackArtwork(props: ArtworkProps) {
  return (
    <ArtworkShell
      {...props}
      accent="M49 31 C55 29 63 31 65 36 C63 42 56 45 49 43 C46 39 46 34 49 31 Z"
      primary="M13 11 C24 9 37 10 44 13 L43 44 C32 41 23 41 13 44 Z"
      secondary="M44 13 C51 15 59 19 65 24 L65 45 C57 42 50 42 43 44 M20 19 C26 18 33 19 37 20 M20 25 C28 24 34 25 38 26"
      detail="M20 32 C27 31 33 32 38 33 M53 19 L53 26 M49 23 L57 23"
    />
  );
}

export function BrandSiteArtwork(props: ArtworkProps) {
  return (
    <ArtworkShell
      {...props}
      accent="M49 13 C55 10 63 13 64 19 C62 24 55 26 49 23 C47 19 47 16 49 13 Z"
      primary="M11 13 C24 11 54 11 68 13 L67 44 C52 46 27 45 12 44 Z"
      secondary="M12 20 C27 19 53 20 67 19 M19 27 C28 25 38 26 43 28 M19 34 C27 33 34 34 39 35"
      detail="M17 16 L18 16 M22 16 L23 16 M27 16 L28 16 M50 31 C56 29 62 31 63 36"
    />
  );
}

export function FounderStoryArtwork(props: ArtworkProps) {
  return (
    <ArtworkShell
      {...props}
      accent="M50 30 C57 27 65 30 66 37 C63 43 56 45 49 42 C47 37 47 33 50 30 Z"
      primary="M28 13 C34 9 42 12 43 19 C43 25 38 29 32 28 C26 26 24 18 28 13 Z M19 44 C21 34 28 30 36 30 C44 30 50 35 52 44"
      secondary="M54 12 C60 13 65 17 67 22 M56 18 C60 19 63 21 65 24"
      detail="M31 20 C34 22 37 22 40 19 M24 40 C31 38 40 38 47 41"
    />
  );
}

export function NewsReactionArtwork(props: ArtworkProps) {
  return (
    <ArtworkShell
      {...props}
      accent="M49 10 C57 8 65 12 66 18 C62 22 56 24 50 22 C47 18 47 13 49 10 Z"
      primary="M12 12 C25 10 45 11 53 13 L52 45 C40 43 25 43 13 45 Z"
      secondary="M20 19 C27 17 37 18 44 20 M20 25 C30 24 39 25 46 26 M20 32 C29 31 38 32 44 33 M57 28 L64 24 L62 34 L69 37 L59 39 L56 47"
      detail="M20 38 C27 37 33 38 38 39"
    />
  );
}

export function PhotoCampaignArtwork(props: ArtworkProps) {
  return (
    <ArtworkShell
      {...props}
      accent="M48 28 C56 25 64 29 65 36 C62 42 55 44 48 41 C46 36 46 31 48 28 Z"
      primary="M11 15 C23 12 39 13 49 16 L47 43 C35 46 21 44 12 42 Z"
      secondary="M25 28 C29 23 34 23 38 29 L47 38 M15 37 L24 29 L32 36 M53 14 C59 12 65 14 68 18 L67 31"
      detail="M18 20 C21 18 24 20 24 23 C22 26 18 25 17 22"
    />
  );
}

export function TestimonialArtwork(props: ArtworkProps) {
  return (
    <ArtworkShell
      {...props}
      accent="M52 29 C58 27 66 30 67 36 C64 42 57 44 51 41 C48 37 49 32 52 29 Z"
      primary="M13 13 C27 10 49 11 61 14 L60 37 C50 40 34 40 24 38 L16 45 L18 37 C14 31 13 22 13 13 Z"
      secondary="M23 21 C31 19 42 20 50 21 M23 27 C32 26 40 27 46 28"
      detail="M30 16 L31 13 M39 16 L40 12"
    />
  );
}

export function WeeklyCalendarArtwork(props: ArtworkProps) {
  return (
    <ArtworkShell
      {...props}
      accent="M45 28 C50 26 57 28 58 33 C56 38 50 39 45 37 C42 34 43 30 45 28 Z"
      primary="M12 15 C26 13 53 13 67 15 L66 45 C52 46 27 45 13 44 Z"
      secondary="M13 23 C29 22 51 23 66 22 M25 11 L25 18 M54 11 L54 18 M27 23 L27 44 M40 23 L40 45 M53 23 L53 44"
      detail="M13 33 C29 32 51 33 66 32"
    />
  );
}

export function BrandVoiceArtwork(props: ArtworkProps) {
  return (
    <ArtworkShell
      {...props}
      accent="M51 15 C58 12 66 16 66 22 C63 27 57 29 51 26 C48 23 48 18 51 15 Z"
      primary="M17 39 C20 25 27 16 36 11 C44 17 49 27 51 40 C41 44 27 44 17 39 Z"
      secondary="M24 30 C31 27 39 27 46 30 M25 36 C32 34 40 34 47 36 M13 17 C18 20 21 23 22 27"
      detail="M56 34 C59 32 63 32 67 34 M57 39 C60 38 64 38 67 39"
    />
  );
}

export function ProductLaunchArtwork(props: ArtworkProps) {
  return (
    <ArtworkShell
      {...props}
      accent="M33 38 C38 40 40 46 37 51 C32 51 29 47 30 42 Z"
      primary="M40 7 C49 12 54 23 50 36 C45 40 36 40 31 36 C28 23 32 12 40 7 Z"
      secondary="M31 29 C25 32 22 37 22 42 C27 40 30 39 34 37 M50 29 C56 32 59 37 59 42 C54 40 51 39 47 37"
      detail="M36 21 C36 17 43 16 44 21 C44 25 37 26 36 21 M59 14 L65 10 M61 20 L69 20"
    />
  );
}

export function LongVideoArtwork(props: ArtworkProps) {
  return (
    <ArtworkShell
      {...props}
      accent="M51 30 C58 27 66 31 66 37 C63 43 56 44 50 41 C48 37 48 33 51 30 Z"
      primary="M11 13 C24 11 47 11 59 14 L58 43 C44 45 26 44 12 42 Z"
      secondary="M32 21 L32 35 L44 28 Z M62 20 C66 22 68 25 69 29 M62 27 C66 30 67 33 67 36"
      detail="M18 18 C27 17 41 18 51 18 M18 38 C28 37 39 38 48 38"
    />
  );
}

export function CommentReplyArtwork(props: ArtworkProps) {
  return (
    <ArtworkShell
      {...props}
      accent="M50 30 C57 27 65 30 66 36 C63 42 56 44 50 41 C47 37 48 33 50 30 Z"
      primary="M11 12 C24 10 43 11 53 14 L52 34 C41 37 29 36 21 34 L14 42 L16 33 C12 27 11 20 11 12 Z"
      secondary="M31 23 C42 21 57 22 65 25 L64 42 C57 44 49 44 42 42 L36 47 L38 40"
      detail="M20 20 C27 18 36 19 43 20 M44 31 C50 30 56 31 60 32"
    />
  );
}

export function PerformanceLoopArtwork(props: ArtworkProps) {
  return (
    <ArtworkShell
      {...props}
      accent="M51 10 C58 8 66 12 66 18 C63 23 57 25 51 22 C48 19 48 14 51 10 Z"
      primary="M13 43 C24 42 37 42 48 43 M17 39 L17 31 L28 27 L36 32 L49 18 L59 23 L68 11"
      secondary="M60 11 L68 11 L67 19 M15 15 C20 11 28 10 34 13 M15 20 C21 17 27 17 32 19"
      detail="M23 43 L23 34 M36 43 L36 32 M49 43 L49 19 M61 43 L61 23"
    />
  );
}

export function UgcHookArtwork(props: ArtworkProps) {
  return (
    <ArtworkShell
      {...props}
      accent="M49 13 C56 10 65 14 65 20 C62 26 55 27 49 24 C46 21 46 16 49 13 Z"
      primary="M25 10 C34 8 45 10 50 14 L49 46 C41 49 29 48 23 44 Z"
      secondary="M18 19 C14 24 14 32 18 37 M57 19 C62 24 62 32 58 37 M31 38 C35 35 40 35 44 38"
      detail="M31 18 C34 16 39 17 41 20 M34 26 L39 26"
    />
  );
}

export function WorkflowCatalogArtwork(props: ArtworkProps) {
  return (
    <ArtworkShell
      {...props}
      accent="M48 10 C55 8 63 11 64 17 C61 22 55 24 49 21 C46 18 46 13 48 10 Z"
      primary="M12 13 C23 11 33 12 39 15 L38 43 C28 41 20 41 12 44 Z M41 15 C49 12 58 12 67 14 L66 44 C57 41 49 41 38 43"
      secondary="M19 21 C25 20 31 20 35 22 M19 27 C26 26 31 27 35 28 M46 27 C53 25 60 26 63 28"
      detail="M46 34 C52 33 58 33 62 35"
    />
  );
}

const CANONICAL_ARTWORK: Record<string, Artwork> = {
  'Article → Multi-Platform Pack': ArticlePackArtwork,
  'Brand Site → Voice + Kit + 5 Starter Posts': BrandSiteArtwork,
  'Founder Story → 4-Week LinkedIn Engine': FounderStoryArtwork,
  'News Reaction Pack': NewsReactionArtwork,
  'Photo Dump → Themed Mini-Campaign': PhotoCampaignArtwork,
  'Testimonial → Social Proof Pack': TestimonialArtwork,
  'Weekly Content Calendar': WeeklyCalendarArtwork,
  'Brand Voice → 10 Hooks': BrandVoiceArtwork,
  'Product Launch Sequence (3-week)': ProductLaunchArtwork,
  'Long Video → Short-Form Posts': LongVideoArtwork,
  'Comment Reply Drafts': CommentReplyArtwork,
  'Performance Loop → Next-Week Strategy': PerformanceLoopArtwork,
  'The UGC 3-Step Viral Hook': UgcHookArtwork,
};

const ICON_ARTWORK: Record<string, Artwork> = {
  'article-multi': ArticlePackArtwork,
  globe: BrandSiteArtwork,
  founder: FounderStoryArtwork,
  newspaper: NewsReactionArtwork,
  photos: PhotoCampaignArtwork,
  quotes: TestimonialArtwork,
  calendar: WeeklyCalendarArtwork,
  brand: BrandVoiceArtwork,
  rocket: ProductLaunchArtwork,
  'chat-bubble': CommentReplyArtwork,
  ugc: UgcHookArtwork,
};

export function resolveWorkflowTemplateArtwork(template: WorkflowTemplate): Artwork {
  const iconId = template.metadata?.icon_id;
  if (iconId === 'newspaper') {
    return template.metadata?.input_kind === 'url' ? LongVideoArtwork : NewsReactionArtwork;
  }
  if (iconId === 'rocket') {
    return template.metadata?.required_brief_keys?.length
      ? ProductLaunchArtwork
      : PerformanceLoopArtwork;
  }
  return ICON_ARTWORK[iconId ?? ''] ?? WorkflowCatalogArtwork;
}

export const WORKFLOW_ARTWORK_CONTACT_SHEET = Object.entries(CANONICAL_ARTWORK);
